import { useState, useEffect, useCallback } from "react";
import { Loader2, Clock } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { API_BASE, TYPE_ICONS, SEVERITY_COLORS, STATUS_COLORS } from "./constants";

export default function TimelineTab({ parcelId, ilotId, ilots = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/timeline/${parcelId}${ilotId ? `?ilot_id=${ilotId}` : ""}`);
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('[TimelineTab] fetch failed:', err);
    }
    setLoading(false);
  }, [parcelId, ilotId]);

  useEffect(() => { fetch_(); }, [fetch_]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>;
  if (!data || data.events.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <Clock className="w-10 h-10 mx-auto mb-3 opacity-20" />
        <p className="text-sm font-medium" style={{ color: "#4A4A4A" }}>Aucun evenement</p>
        <p className="text-xs mt-1" style={{ color: "#7A7A7A" }}>Ajoutez des interventions, fertilisations ou observations</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {[
            { label: "Phyto", count: data.stats.phyto_count, color: "#2D6A4F" },
            { label: "Ferti", count: data.stats.fertilisation_count, color: "#8B5E3C" },
            { label: "Sol", count: data.stats.analyses_sol_count, color: "#6366F1" },
            { label: "Sanitaire", count: data.stats.sanitaire_count, color: "#E07A5F" },
            { label: "Bio", count: data.stats.biostimulant_count, color: "#0891B2" },
            { label: "Irrigation", count: data.stats.irrigation_count, color: "#2B7BB9" },
            { label: "Plantation", count: data.stats.plantation_count, color: "#3F7D3A" },
            { label: "Récolte", count: data.stats.recolte_count, color: "#9A6A12" },
          ].filter(s => s.count > 0).map(s => (
            <Badge key={s.label} className="text-[10px] font-semibold border-0 py-0.5 px-2" style={{ background: `${s.color}12`, color: s.color }}>
              {s.count} {s.label}
            </Badge>
          ))}
          {data.stats.sanitaire_actif > 0 && (
            <Badge className="text-[10px] font-semibold border-0 py-0.5 px-2" style={{ background: "#EF444415", color: "#DC2626" }}>
              {data.stats.sanitaire_actif} alerte{data.stats.sanitaire_actif > 1 ? 's' : ''} active{data.stats.sanitaire_actif > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <div className="relative">
          <div className="absolute left-[19px] top-0 bottom-0 w-px bg-black/10" />
          {data.events.map((evt) => {
            const typeInfo = TYPE_ICONS[evt.type] || TYPE_ICONS.phyto;
            const Icon = typeInfo.icon;
            return (
              <div key={`${evt.type}-${evt.id}`} className="relative flex gap-3 pb-4" data-testid={`timeline-event-${evt.id}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10 border-2 border-white" style={{ background: typeInfo.bg }}>
                  <Icon className="w-4 h-4" style={{ color: typeInfo.color }} />
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>{evt.title}</p>
                    {evt.severity && (
                      <Badge className="text-[9px] border-0 py-0 px-1.5" style={{ background: SEVERITY_COLORS[evt.severity]?.bg, color: SEVERITY_COLORS[evt.severity]?.text }}>
                        {SEVERITY_COLORS[evt.severity]?.label}
                      </Badge>
                    )}
                    {evt.status && (
                      <Badge className="text-[9px] border-0 py-0 px-1.5" style={{ background: STATUS_COLORS[evt.status]?.bg, color: STATUS_COLORS[evt.status]?.text }}>
                        {STATUS_COLORS[evt.status]?.label}
                      </Badge>
                    )}
                    {evt.data?.ilot_id && ilotName(evt.data.ilot_id) && (
                      <Badge className="text-[9px] border-0 py-0 px-1.5" style={{ background: "#dbe8f1", color: "#2f6a92" }}>
                        {ilotName(evt.data.ilot_id)}
                      </Badge>
                    )}
                  </div>
                  {evt.subtitle && <p className="text-[11px] mt-0.5" style={{ color: "#7A7A7A" }}>{evt.subtitle}</p>}
                  <p className="text-[10px] mt-0.5" style={{ color: "#AAAAAA" }}>{evt.date}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}
